/**
 * WebSocket Client with Flexible Authentication
 * Supports both frontend and backend authentication strategies
 */

/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/no-parameter-properties */

import {
  AuthStrategy,
  AudioConfig,
  ConnectionConfig,
  TranscriptionSegment
} from '../core/types'

/**
 * Token-based Authentication Strategy
 * Frontend calls /websocket endpoint to get authenticated WSS URL
 */
export class TokenAuthStrategy implements AuthStrategy {
  constructor(
    private apiBase: string,
    private getToken: () => Promise<string>,
    private engine: string = 'voxist-rt'
  ) {}

  async getWebSocketUrl(): Promise<string> {
    const token = await this.getToken()
    const response = await fetch(
      `${this.apiBase}/websocket?engine=${this.engine}`,
      {
        method: 'GET',
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Authorization: `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to get WebSocket URL: ${response.status} ${response.statusText}`)
    }

    const { url } = await response.json()
    return url
  }
}

/**
 * Direct URL Authentication Strategy
 * Backend pre-generates authenticated WSS URL, frontend uses it directly
 */
export class DirectUrlAuthStrategy implements AuthStrategy {
  constructor(private wsUrl: string) {}

  async getWebSocketUrl(): Promise<string> {
    return this.wsUrl
  }
}

/**
 * WebSocket Client for real-time audio transcription
 *
 * Features:
 * - Flexible authentication (frontend or backend)
 * - Binary audio streaming
 * - JSON message handling for transcriptions
 * - Connection lifecycle management
 * - Error handling and reconnection support
 *
 * Protocol:
 * 1. Connect to WebSocket URL
 * 2. Send config message (JSON)
 * 3. Stream audio data (binary)
 * 4. Receive transcriptions (JSON)
 * 5. Send 'Done' signal to finalize
 */
export class WebSocketClient {
  private ws: WebSocket | null = null
  private isConnected: boolean = false
  private callbacks: ConnectionConfig = {}

  constructor(
    private authStrategy: AuthStrategy,
    private audioConfig: AudioConfig
  ) {}

  /**
   * Connect to WebSocket server
   * @throws Error if connection fails
   */
  async connect(): Promise<void> {
    const wsUrl = await this.authStrategy.getWebSocketUrl()

    this.ws = new WebSocket(wsUrl)

    return new Promise((resolve, reject) => {
      if (!this.ws) {
        reject(new Error('Failed to create WebSocket'))
        return
      }

      this.ws.onopen = () => {
        this.isConnected = true
        this.callbacks.onConnect?.()
        resolve()
      }

      this.ws.onmessage = (event) => {
        this.handleMessage(event)
      }

      this.ws.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket error:', error)
        this.callbacks.onError?.('WebSocket connection error')
        reject(error)
      }

      this.ws.onclose = (event) => {
        this.isConnected = false
        this.callbacks.onDisconnect?.()

        // Check for abnormal closures
        if (event.code === 1006 || event.code === 1011) {
          this.callbacks.onError?.(`Connection lost: ${event.code} ${event.reason}`)
        }
      }
    })
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    if (typeof event.data === 'string') {
      try {
        const data = JSON.parse(event.data)

        // Handle transcription messages
        if (data.type === 'partial' || data.type === 'final') {
          this.callbacks.onMessage?.(data as TranscriptionSegment)
        }
        // Handle status messages
        else if (data.status === 'connected') {
          // eslint-disable-next-line no-console
          console.log('ASR engine connected')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing message:', error)
      }
    }
  }

  /**
   * Start streaming by sending configuration
   */
  startStreaming(): void {
    if (!this.isConnected || !this.ws) {
      throw new Error('WebSocket not connected')
    }

    const config = {
      config: {
        lang: this.audioConfig.language,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        sample_rate: this.audioConfig.sampleRate,
        model: this.audioConfig.model || 'banafo',
        ...(this.audioConfig.punctuationMode && {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          punctuation_mode: this.audioConfig.punctuationMode
        })
      }
    }

    this.ws.send(JSON.stringify(config))
  }

  /**
   * Send binary audio data
   * @param audioData Int16 PCM audio buffer
   */
  sendAudioData(audioData: ArrayBuffer): void {
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(audioData)
    }
  }

  /**
   * Signal end of audio stream
   */
  endStreaming(): void {
    if (this.isConnected && this.ws) {
      this.ws.send('Done')
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close()
      this.ws = null
      this.isConnected = false
    }
  }

  /**
   * Register message callback
   * @param callback Function to call on transcription messages
   */
  onMessage(callback: (response: TranscriptionSegment) => void): void {
    this.callbacks.onMessage = callback
  }

  /**
   * Register error callback
   * @param callback Function to call on errors
   */
  onError(callback: (error: string) => void): void {
    this.callbacks.onError = callback
  }

  /**
   * Register connect callback
   * @param callback Function to call on successful connection
   */
  onConnect(callback: () => void): void {
    this.callbacks.onConnect = callback
  }

  /**
   * Register disconnect callback
   * @param callback Function to call on disconnection
   */
  onDisconnect(callback: () => void): void {
    this.callbacks.onDisconnect = callback
  }

  /**
   * Check if WebSocket is connected and ready
   */
  isWebSocketConnected(): boolean {
    return this.isConnected && this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * Get current WebSocket state
   * @returns 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
   */
  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }
}
